import { type Ref, unref, watch } from 'vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { logger } from '@tools/logging';
import { defaultMessage, defaultSolution, vermelding } from './helpers';
import { type ErrorHandlerOptions, ErrorSeverity, type OnError } from './types';
import {
  BaseError,
  Conflict,
  ForbiddenError,
  isSbAxiosError,
  NotFoundError,
  type SbError,
  TimeoutError,
  ValidationError,
} from './sbError';

/*
 * Hiermee kan je een error afhandelen en een notificatie tonen.
 * Standaard wordt er een notificatie getoond met de message van de error.
 * Je kan ook een custom message meegeven. Voor alle errors via onError
 * Als je een specifieke error een andere error of afhandeling wil geven kan dat via de overige handlers
 * default message kan je ook aanpassen deze wordt gebruikt als er in de title geen error gevonden wordt.
 */
export function useWatchErrorHandler(
  errorRef: Ref<Error> | Ref<null>,
  options: ErrorHandlerOptions = {},
) {
  const { handleError } = useErrorHandler();
  watch(errorRef, (error) => handleError(error, options));
}

export function generateValidationErrorsList(error: ValidationError): string {
  if (!error.problemDetails?.errors || Object.keys(error.problemDetails.errors).length === 0) {
    return '';
  }

  const listItems = Object.values(error.problemDetails.errors)
    .flat()
    .map((msg) => `<li>${msg}</li>`)
    .join('');

  return listItems ? `<ul>${listItems}</ul>` : '';
}

export function useErrorHandler() {
  const notification = useNotification();

  function formatNotification(
    error: SbError,
    opts: { customMessage?: string; customSolution?: string } = {},
  ) {
    const message = opts.customMessage || (error.message ?? defaultMessage);

    const referentie = error.correlationId ? vermelding(error.correlationId) : '';
    const solution = opts.customSolution || defaultSolution;
    const validationErrors =
      error instanceof ValidationError ? generateValidationErrorsList(error) : '';

    return `<p>${message}</p>${validationErrors}<p>${solution} ${referentie}</p>`;
  }

  const showNotification = (
    error: SbError,
    opts: { customMessage?: string; customSolution?: string; severity?: ErrorSeverity } = {},
  ) => {
    if (opts.severity === ErrorSeverity.Warning) {
      notification.warning(formatNotification(error, opts));
    } else {
      notification.error(formatNotification(error, opts));
    }
  };

  function onHandleError<T extends SbError>(
    error: T,
    onErrorHandler: OnError<T>,
    severity?: ErrorSeverity,
  ) {
    const unwrappedOnErrorHandler = unref(onErrorHandler);
    if (typeof unwrappedOnErrorHandler === 'string') {
      showNotification(error, { customMessage: unwrappedOnErrorHandler, severity });
    } else {
      unwrappedOnErrorHandler(error);
    }
  }

  function handleSbError(error: SbError, opts: ErrorHandlerOptions) {
    if (error instanceof ValidationError && opts.onValidationError) {
      onHandleError(error, opts.onValidationError);
    } else if (error instanceof ForbiddenError && opts.onForbiddenError) {
      onHandleError(error, opts.onForbiddenError);
    } else if (error instanceof NotFoundError && opts.onNotFoundError) {
      onHandleError(error, opts.onNotFoundError);
    } else if (error instanceof Conflict && opts.onConflictError) {
      onHandleError(error, opts.onConflictError);
    } else if (error instanceof TimeoutError && opts.onTimeoutError) {
      onHandleError(error, opts.onTimeoutError);
    } else if (opts.onError) {
      onHandleError(error, opts.onError, opts.severity);
    } else {
      showNotification(error, { severity: opts.severity });
    }
  }

  function handleError(error: Error | SbError | null, opts: ErrorHandlerOptions = {}) {
    logger.debug('handleError', error);
    if (!error) return;

    if (isSbAxiosError(error)) {
      handleSbError(error.sbError, opts);
      return;
    }

    if (error instanceof BaseError) {
      handleSbError(error, opts);
      return;
    }

    notification.error(`${defaultMessage}<br />${defaultSolution}`);
  }

  return {
    handleError,
    showNotification,
  };
}
